/**
 * Authentication service
 */

import { apiGet, apiPost } from './api';
import type { User } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Auth response from email/password endpoints
 */
export interface AuthResponse {
  message: string;
}

/**
 * Register request data
 */
export interface RegisterData {
  email: string;
  password: string;
  display_name: string;
  invite_token?: string;
  referral_code?: string;
}

/**
 * Login request data
 */
export interface LoginData {
  email: string;
  password: string;
}

/**
 * Build OAuth query params from invite token and/or referral code
 */
function buildOAuthParams(inviteToken?: string, referralCode?: string): string {
  const params = new URLSearchParams();
  if (inviteToken) params.set('invite', inviteToken);
  if (referralCode) params.set('ref', referralCode);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Redirect to Google OAuth login
 */
export function loginWithGoogle(inviteToken?: string, referralCode?: string): void {
  window.location.href = `${API_BASE_URL}/api/auth/google${buildOAuthParams(inviteToken, referralCode)}`;
}

/**
 * Redirect to Discord OAuth login
 */
export function loginWithDiscord(inviteToken?: string, referralCode?: string): void {
  window.location.href = `${API_BASE_URL}/api/auth/discord${buildOAuthParams(inviteToken, referralCode)}`;
}

/**
 * Redirect to Microsoft OAuth login
 */
export function loginWithMicrosoft(inviteToken?: string, referralCode?: string): void {
  window.location.href = `${API_BASE_URL}/api/auth/microsoft${buildOAuthParams(inviteToken, referralCode)}`;
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser() {
  return apiGet<User>('/api/auth/me');
}

/**
 * Logout current user
 */
export async function logout() {
  return apiPost<void>('/api/auth/logout');
}

/**
 * Link additional OAuth provider to account
 */
export async function linkOAuthProvider(provider: 'google' | 'discord' | 'microsoft') {
  window.location.href = `${API_BASE_URL}/api/auth/link/${provider}`;
}

/**
 * Register a new user with email/password
 */
export async function register(data: RegisterData) {
  return apiPost<AuthResponse, RegisterData>('/api/auth/register', data, { auth: false });
}

/**
 * Login with email/password
 */
export async function loginWithEmail(data: LoginData) {
  return apiPost<AuthResponse, LoginData>('/api/auth/login', data, { auth: false });
}

/**
 * Verify email with token
 */
export async function verifyEmail(token: string) {
  return apiPost<AuthResponse, { token: string }>('/api/auth/verify-email', { token }, { auth: false });
}

/**
 * Request password reset
 */
export async function forgotPassword(email: string) {
  return apiPost<AuthResponse, { email: string }>('/api/auth/forgot-password', { email }, { auth: false });
}

/**
 * Reset password with token
 */
export async function resetPassword(token: string, password: string) {
  return apiPost<AuthResponse, { token: string; password: string }>(
    '/api/auth/reset-password',
    { token, password },
    { auth: false }
  );
}

/**
 * Resend verification email
 */
export async function resendVerification(email: string) {
  return apiPost<AuthResponse, { email: string }>('/api/auth/resend-verification', { email }, { auth: false });
}

/**
 * Registration status response
 */
export interface RegistrationStatus {
  enabled: boolean;
  disabled_message: string | null;
}

/**
 * Get registration status
 */
export async function getRegistrationStatus() {
  return apiGet<RegistrationStatus>('/api/admin/settings/public/registration-status', { auth: false });
}

/**
 * Waitlist request data
 */
export interface WaitlistRequest {
  email: string;
}

/**
 * Join waitlist
 */
export async function joinWaitlist(data: WaitlistRequest) {
  return apiPost<AuthResponse, WaitlistRequest>('/api/auth/waitlist', data, { auth: false });
}
